// Activity Feed Component
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getActivityFeed,
  subscribeToActivityFeed,
  getNearbyUserActivities,
  getConnectionActivities,
  ACTIVITY_TYPES
} from '../services/activityService';
import { getUsernameFromWallet } from '../userMappingService';
import { formatAddress } from '../cryptoOperations';
import { getGroup } from '../services/groupsService';

const ActivityFeed = ({ walletAddress, userLocation, onClose, onOpenMemberProfile, onOpenGroup, isMobile = false }) => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'nearby', 'connections', 'groups'
  const [userProfiles, setUserProfiles] = useState({});
  const [groupCache, setGroupCache] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  const notificationTimeoutRef = useRef(null);

  // Load activities based on filter
  useEffect(() => {
    if (!walletAddress) {
      setActivities([]);
      setIsLoading(false);
      return;
    }

    const loadActivities = async () => {
      try {
        setIsLoading(true);
        let loadedActivities = [];

        switch (activeFilter) {
          case 'nearby':
            if (userLocation) {
              loadedActivities = await getNearbyUserActivities(userLocation, 50, 30);
            } else {
              loadedActivities = [];
            }
            break;
          case 'connections':
            loadedActivities = await getConnectionActivities(walletAddress, 30);
            break;
          case 'groups':
            loadedActivities = await getActivityFeed(walletAddress, {
              limitCount: 30,
              activityTypes: [ACTIVITY_TYPES.GROUP_POST, ACTIVITY_TYPES.GROUP_EVENT]
            });
            break;
          default: // 'all'
            loadedActivities = await getActivityFeed(walletAddress, {
              limitCount: 50
            });
            break;
        }

        setActivities(loadedActivities);
      } catch (error) {
        console.error('Error loading activities:', error);
        setActivities([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();

    // Subscribe to real-time updates for 'all' filter
    let unsubscribe = null;
    if (activeFilter === 'all') {
      unsubscribe = subscribeToActivityFeed(walletAddress, (updatedActivities) => {
        setActivities(updatedActivities);
      }, {
        limitCount: 50
      });
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [walletAddress, activeFilter, userLocation]);

  // Load usernames for activities
  useEffect(() => {
    const loadUsernames = async () => {
      const addresses = new Set();
      
      activities.forEach(activity => {
        if (activity.actor) {
          addresses.add(activity.actor.toLowerCase());
        }
        // Also get usernames from data fields
        if (activity.data?.userAddress) {
          addresses.add(activity.data.userAddress.toLowerCase());
        }
        if (activity.data?.author) {
          addresses.add(activity.data.author.toLowerCase());
        }
      });

      const usernamePromises = Array.from(addresses).map(async (addr) => {
        if (!userProfiles[addr]) {
          try {
            const username = await getUsernameFromWallet(addr);
            return { addr, username: username || formatAddress(addr) };
          } catch (error) {
            return { addr, username: formatAddress(addr) };
          }
        }
        return null;
      });

      const results = await Promise.all(usernamePromises);
      const newProfiles = {};
      results.forEach(result => {
        if (result) {
          newProfiles[result.addr] = { username: result.username };
        }
      });

      if (Object.keys(newProfiles).length > 0) {
        setUserProfiles(prev => ({ ...prev, ...newProfiles }));
      }
    };

    if (activities.length > 0) {
      loadUsernames();
    }
  }, [activities]);

  // Load group names for group activities
  useEffect(() => {
    const loadGroups = async () => {
      const groupIds = new Set();
      
      activities.forEach(activity => {
        if (activity.type === ACTIVITY_TYPES.GROUP_POST || activity.type === ACTIVITY_TYPES.GROUP_EVENT) {
          if (activity.data?.groupId) {
            groupIds.add(activity.data.groupId);
          }
        }
      });

      const groupPromises = Array.from(groupIds).map(async (groupId) => {
        if (!groupCache[groupId]) {
          try {
            const group = await getGroup(groupId);
            return { groupId, group };
          } catch (error) {
            return { groupId, group: null };
          }
        }
        return null;
      });

      const results = await Promise.all(groupPromises);
      const newGroups = {};
      results.forEach(result => {
        if (result && result.group) {
          newGroups[result.groupId] = result.group;
        }
      });

      if (Object.keys(newGroups).length > 0) {
        setGroupCache(prev => ({ ...prev, ...newGroups }));
      }
    };

    if (activities.length > 0) {
      loadGroups();
    }
  }, [activities]);

  // Helper to get display name
  const getDisplayName = (walletAddr) => {
    if (!walletAddr) return 'Unknown';
    const normalizedAddr = walletAddr.toLowerCase();
    return userProfiles[normalizedAddr]?.username || formatAddress(walletAddr);
  };

  // Format relative date
  const formatRelativeDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get activity icon
  const getActivityIcon = (type) => {
    switch (type) {
      case ACTIVITY_TYPES.USER_JOINED:
        return '👋';
      case ACTIVITY_TYPES.GROUP_POST:
        return '📝';
      case ACTIVITY_TYPES.GROUP_EVENT:
        return '📅';
      case ACTIVITY_TYPES.PROFILE_UPDATE:
        return '✏️';
      case ACTIVITY_TYPES.MESSAGE_STARTED:
        return '💬';
      case ACTIVITY_TYPES.GROUP_JOINED:
        return '➕';
      default:
        return '🔔';
    }
  };

  // Get activity description
  const getActivityDescription = (activity) => {
    const actorName = getDisplayName(activity.actor);
    const timestamp = activity.createdAt || (activity.timestamp ? new Date(activity.timestamp) : null);
    const timeAgo = timestamp ? formatRelativeDate(timestamp) : '';

    switch (activity.type) {
      case ACTIVITY_TYPES.USER_JOINED:
        const location = activity.data?.location;
        const locationText = location ? ` in ${location.city || 'your area'}` : '';
        return `${actorName} joined nearby${locationText}`;
      
      case ACTIVITY_TYPES.GROUP_POST:
        const group = activity.data?.groupId ? groupCache[activity.data.groupId] : null;
        const groupName = group?.name || 'a group';
        const postPreview = activity.data?.content ? 
          (activity.data.content.length > 50 ? activity.data.content.substring(0, 50) + '...' : activity.data.content) : 
          'a post';
        return `${actorName} posted "${postPreview}" in ${groupName}`;
      
      case ACTIVITY_TYPES.GROUP_EVENT:
        const eventGroup = activity.data?.groupId ? groupCache[activity.data.groupId] : null;
        const eventGroupName = eventGroup?.name || 'a group';
        const eventTitle = activity.data?.title || 'an event';
        return `${actorName} created event "${eventTitle}" in ${eventGroupName}`;
      
      case ACTIVITY_TYPES.PROFILE_UPDATE:
        return `${actorName} updated their profile`;
      
      case ACTIVITY_TYPES.MESSAGE_STARTED:
        return `${actorName} started a conversation`;
      
      case ACTIVITY_TYPES.GROUP_JOINED:
        const joinedGroup = activity.data?.groupId ? groupCache[activity.data.groupId] : null;
        const joinedGroupName = joinedGroup?.name || 'a group';
        return `${actorName} joined ${joinedGroupName}`;
      
      default:
        return `${actorName} performed an action`;
    }
  };

  // Handle activity click
  const handleActivityClick = (activity) => {
    switch (activity.type) {
      case ACTIVITY_TYPES.USER_JOINED:
      case ACTIVITY_TYPES.PROFILE_UPDATE:
      case ACTIVITY_TYPES.MESSAGE_STARTED:
        if (onOpenMemberProfile && activity.actor) {
          onOpenMemberProfile(activity.actor);
        }
        break;
      
      case ACTIVITY_TYPES.GROUP_POST:
      case ACTIVITY_TYPES.GROUP_EVENT:
      case ACTIVITY_TYPES.GROUP_JOINED:
        if (onOpenGroup && activity.data?.groupId) {
          onOpenGroup(activity.data.groupId);
        }
        break;
    }
  };

  const containerStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    left: isMobile ? 0 : 'auto',
    width: isMobile ? '100%' : '400px',
    height: '100vh',
    backgroundColor: '#fff',
    borderLeft: isMobile ? 'none' : '1px solid #e0e0e0',
    overflowY: 'auto',
    zIndex: 1015,
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <div id="activity-feed-section" style={containerStyle} className={isMobile ? 'mobile-panel' : ''}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f9f9f9',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Activity Feed</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setShowHelp(!showHelp)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '1px solid #ccc',
                backgroundColor: showHelp ? '#f0f0f0' : '#fff',
                color: '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.borderColor = '#999';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = showHelp ? '#f0f0f0' : '#fff';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              title="How Activity Feed Works"
            >
              ?
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0',
                color: '#666'
              }}
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Help Section */}
        {showHelp && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            fontSize: '13px',
            lineHeight: '1.6',
            color: '#333'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
              How Activity Feed Works
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ marginTop: 0, marginBottom: '8px' }}>
                The <strong>Activity Feed</strong> shows you real-time updates about what's happening in your PeopleTag community. Stay connected with the latest activities from people around you.
              </p>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '6px' }}><strong>All:</strong> See all activities from the entire community</li>
                <li style={{ marginBottom: '6px' }}><strong>Nearby:</strong> View activities from users in your geographic area (requires location enabled)</li>
                <li style={{ marginBottom: '6px' }}><strong>Connections:</strong> Activities from people you've messaged or connected with</li>
                <li style={{ marginBottom: '6px' }}><strong>Groups:</strong> Posts and events from groups you're part of</li>
              </ul>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ marginTop: 0, marginBottom: '8px', fontWeight: '600' }}>Activity Types:</p>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '6px' }}>👋 <strong>User Joined:</strong> New users joining nearby</li>
                <li style={{ marginBottom: '6px' }}>📝 <strong>Group Post:</strong> New posts in groups you follow</li>
                <li style={{ marginBottom: '6px' }}>📅 <strong>Group Event:</strong> Events created in groups</li>
                <li style={{ marginBottom: '6px' }}>✏️ <strong>Profile Update:</strong> People updating their profiles</li>
                <li style={{ marginBottom: '6px' }}>💬 <strong>Message Started:</strong> New conversations beginning</li>
                <li style={{ marginBottom: '6px' }}>➕ <strong>Group Joined:</strong> Users joining groups</li>
              </ul>
            </div>
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
              <p style={{ margin: '0 0 6px 0' }}><strong>Tips:</strong></p>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Click on any activity to view the profile or group</li>
                <li>The feed updates in real-time for the "All" filter</li>
                <li>Activities show relative time (e.g., "2h ago") and distance when available</li>
                <li>Enable location services to see nearby activities</li>
              </ul>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'nearby', label: 'Nearby' },
            { key: 'connections', label: 'Connections' },
            { key: 'groups', label: 'Groups' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              style={{
                flex: 1,
                padding: '6px 8px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: activeFilter === filter.key ? '#4a90e2' : '#f0f0f0',
                color: activeFilter === filter.key ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading activities...
          </div>
        ) : activities.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <p>No activities to show.</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>
              {activeFilter === 'nearby' && !userLocation && 'Enable location to see nearby activities'}
              {activeFilter === 'connections' && 'Start messaging to see connection activities'}
              {activeFilter === 'groups' && 'Join groups to see group activities'}
              {activeFilter === 'all' && 'Activity will appear here as users interact'}
            </p>
          </div>
        ) : (
          <div style={{ padding: '12px' }}>
            {activities.map(activity => (
              <div
                key={activity.id || activity.activityId}
                onClick={() => handleActivityClick(activity)}
                style={{
                  padding: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: '#fff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                  e.currentTarget.style.borderColor = '#4a90e2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ fontSize: '24px', flexShrink: 0 }}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', lineHeight: '1.4', marginBottom: '4px' }}>
                      {getActivityDescription(activity)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#999' }}>
                      {formatRelativeDate(activity.createdAt || (activity.timestamp ? new Date(activity.timestamp) : null))}
                      {activity.distance && ` • ${activity.distance.toFixed(1)} km away`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;

